import { useCallback, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import { Button, Spinner, Tooltip, toast } from "@altimateai/lego";
import {
  buildTestNotificationPayload,
  getTestPayloadBlocker,
} from "./utils/subscriptionTransformers";
import { useTestNotification } from "./services/wizardApi";
import { useSlackChannels } from "@/pages/alerts/useSlackChannels";
import { ChannelType } from "@/pages/subscriptions/constants";
import type { SubscriptionFormData, TestNotifier } from "@/pages/subscriptions/services/types";

interface SendTestButtonProps {
  control: Control<SubscriptionFormData>;
}

interface PlannedSend {
  notifier: TestNotifier;
  successCopy: string;
  errorLabel: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const mapTestError = (rawMessage: string, fallbackLabel: string): string => {
  const msg = (rawMessage ?? "").trim();
  if (/Notification\s+\d+\s+not\s+found/i.test(msg)) {
    return "This Slack channel was deleted. Pick another from the dropdown.";
  }
  if (/slack\s+delivery\s+failed/i.test(msg)) {
    return "Slack rejected the message — the webhook may have been revoked.";
  }
  if (/\b429\b/.test(msg) || /too\s+many/i.test(msg)) {
    return "Too many test sends — wait a minute and try again.";
  }
  return msg || `Failed to send test to ${fallbackLabel}.`;
};

const planSends = (
  formData: SubscriptionFormData,
  channelNameLookup: (id: string) => string | null
): { sends: PlannedSend[]; blocker: string | null } => {
  const channels = formData.channels ?? [];
  const recipients = (formData.email_recipients ?? []).map(e => e.trim()).filter(Boolean);
  const slackIds = formData.slack_channels ?? [];

  const sends: PlannedSend[] = [];

  if (channels.includes(ChannelType.EMAIL)) {
    if (recipients.length === 0) {
      return { sends: [], blocker: "Add at least one email recipient." };
    }
    if (!recipients.every(r => EMAIL_REGEX.test(r))) {
      return { sends: [], blocker: "Fix invalid email addresses before sending a test." };
    }
    sends.push({
      notifier: { type: ChannelType.EMAIL, recipients },
      successCopy: `Test email sent to ${recipients.join(", ")}.`,
      errorLabel: recipients.join(", "),
    });
  }

  if (channels.includes(ChannelType.SLACK)) {
    if (slackIds.length === 0) {
      return { sends: [], blocker: "Pick a Slack channel first." };
    }
    for (const id of slackIds) {
      const label = channelNameLookup(id) ?? `channel ${id}`;
      sends.push({
        notifier: { type: ChannelType.SLACK, channel: id },
        successCopy: `Test message posted to ${label}.`,
        errorLabel: label,
      });
    }
  }

  if (sends.length === 0) {
    return { sends: [], blocker: "Select Email or Slack to send a test." };
  }

  return { sends, blocker: null };
};

const SendTestButton = ({ control }: SendTestButtonProps) => {
  const formData = useWatch({ control }) as SubscriptionFormData;
  const { slackChannels } = useSlackChannels();
  const testMutation = useTestNotification();
  const [isSending, setIsSending] = useState(false);

  const channelNameLookup = useCallback(
    (id: string) => {
      const channel = slackChannels?.find(ch => ch.id === id);
      return channel ? `#${channel.config.name}` : null;
    },
    [slackChannels]
  );

  const payloadBlocker = getTestPayloadBlocker(formData);
  const { sends, blocker: destinationBlocker } = planSends(formData, channelNameLookup);
  const blocker = payloadBlocker ?? destinationBlocker;
  const disabled = blocker !== null || isSending;

  const handleClick = useCallback(async () => {
    if (sends.length === 0) return;

    setIsSending(true);
    try {
      const results = await Promise.all(
        sends.map(async send => {
          try {
            const payload = buildTestNotificationPayload(formData, send.notifier);
            const response = await testMutation.mutateAsync(payload);
            return { send, response, error: null as unknown };
          } catch (error: unknown) {
            return { send, response: undefined, error };
          }
        })
      );

      const successCopies: string[] = [];
      const failureLines: string[] = [];

      for (const { send, response, error } of results) {
        if (error !== null) {
          const message = typeof error === "string" ? error : ((error as Error)?.message ?? "");
          failureLines.push(`${send.errorLabel}: ${mapTestError(message, send.errorLabel)}`);
        } else if (response?.ok) {
          successCopies.push(send.successCopy);
        } else {
          failureLines.push(
            `${send.errorLabel}: ${mapTestError(response?.error ?? "", send.errorLabel)}`
          );
        }
      }

      // Consolidate per-click into at most one success + one failure toast so multiple
      // notifiers don't blow out a stack of rapid-fire toasts.
      if (successCopies.length > 0) {
        toast({ title: "Test sent", description: successCopies.join(" ") });
      }
      if (failureLines.length > 0) {
        toast({
          variant: "destructive",
          title: failureLines.length === results.length ? "Test failed" : "Some tests failed",
          description: failureLines.join(" — "),
        });
      }
    } finally {
      setIsSending(false);
    }
  }, [sends, formData, testMutation]);

  const button = (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      onClick={handleClick}
      aria-label="Send preview to destination"
    >
      {isSending ? <Spinner className="al-mr-2" size="sm" /> : null}
      Send preview to destination
    </Button>
  );

  if (blocker) {
    return <Tooltip title={blocker}>{button}</Tooltip>;
  }

  return button;
};

export default SendTestButton;
